import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight,
  FileText,
  Search,
  DollarSign,
  Home,
  X,
  Calendar,
  Settings
} from 'lucide-react';
import api from '../../services/api';
import { useAuthContext } from '../../contexts/AuthContext';
import ApiHealthIndicator from '../ApiHealthIndicator';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface MenuCategory {
  name: string;
  icon: string;
  items: MenuItem[];
}

interface SidebarProps {
  userRole: string;
  currentPath: string;
  onNavigate: (path: string) => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ userRole, currentPath, onNavigate, isMobileOpen = false, onMobileClose }) => {
  const { token } = useAuthContext();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['Dashboard'])); // Dashboard expanded by default
  const [loading, setLoading] = useState(true);

  // Icon mapping
  const getIcon = (iconName: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      'Home': <Home className="w-4 h-4" />,
      'FileText': <FileText className="w-4 h-4" />,
      'DollarSign': <DollarSign className="w-4 h-4" />,
      'Search': <Search className="w-4 h-4" />,
      'Calendar': <Calendar className="w-4 h-4" />,
      'Settings': <Settings className="w-4 h-4" />
    };
    return icons[iconName] || <FileText className="w-4 h-4" />;
  };

  // Fetch menu items from API
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const response = await api.get('/role-pages/menu');
        if (response.data.success) {
          const data = response.data.data;
          
          // Handle new categorized structure
          if (data.categories) {
            setMenuCategories(data.categories.map((category: any) => ({
              name: category.name,
              icon: category.icon,
              items: category.items.map((item: any) => ({
                path: item.path,
                label: item.label,
                icon: getIcon(item.icon)
              }))
            })));
          }
          
          // Backward compatibility - flatten menu items
          const items = data.menu_items?.map((item: any) => ({
            path: item.path,
            label: item.label,
            icon: getIcon(item.icon)
          })) || [];
          setMenuItems(items);
        }
      } catch (error) {
        console.error('Error fetching menu items:', error);
        // Fallback to empty menu
        setMenuItems([]);
        setMenuCategories([]);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch when we have a token (authentication is complete)
    if (token) {
      fetchMenuItems();
    }
    
    // Listen for storage changes (when JWT token is updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'jwt_token' && e.newValue) {
        fetchMenuItems();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [token]); // Depend on token from AuthContext

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  const accessibleItems = menuItems;

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        bg-white border-r border-gray-200 transition-all duration-300 flex flex-col h-screen
        ${isCollapsed ? 'w-16' : 'w-64'}
        fixed inset-y-0 left-0 z-50
        lg:relative lg:translate-x-0
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        {!isCollapsed && (
          <h2 className="text-lg font-semibold text-gray-900">Wynder</h2>
        )}
        <div className="flex items-center gap-2">
          {/* Mobile Close Button */}
          <button
            onClick={onMobileClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          
          {/* Desktop Collapse Button */}
          <button
            onClick={() => {
              const newCollapsed = !isCollapsed;
              setIsCollapsed(newCollapsed);
              localStorage.setItem('sidebar-collapsed', JSON.stringify(newCollapsed));
            }}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors hidden lg:block"
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        <nav className="space-y-1 px-2">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : menuCategories.length > 0 ? (
            // Render categorized menu
            menuCategories.map(category => (
              <div key={category.name} className="mb-2">
                {!isCollapsed && (
                  <button
                    onClick={() => toggleCategory(category.name)}
                    className="w-full flex items-center justify-between px-2 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700"
                  >
                    <div className="flex items-center">
                      {getIcon(category.icon)}
                      <span className="ml-2">{category.name}</span>
                    </div>
                    <span className={`transform transition-transform ${
                      expandedCategories.has(category.name) ? 'rotate-90' : ''
                    }`}>
                      ▶
                    </span>
                  </button>
                )}
                
                {(isCollapsed || expandedCategories.has(category.name)) && (
                  <div className={isCollapsed ? '' : 'ml-4'}>
                    {category.items.map(item => (
                      <button
                        key={item.path}
                        onClick={() => {
                          onNavigate(item.path);
                          onMobileClose?.();
                        }}
                        className={`w-full flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-colors ${
                          currentPath === item.path
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        title={isCollapsed ? item.label : undefined}
                      >
                        {item.icon}
                        {!isCollapsed && (
                          <span className="ml-3">{item.label}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : accessibleItems.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              No accessible pages
            </div>
          ) : (
            // Fallback to flat menu for backward compatibility
            accessibleItems.map(item => (
              <button
                key={item.path}
                onClick={() => {
                  onNavigate(item.path);
                  onMobileClose?.();
                }}
                className={`w-full flex items-center px-2 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentPath === item.path
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                {item.icon}
                {!isCollapsed && (
                  <span className="ml-3">{item.label}</span>
                )}
              </button>
            ))
          )}
        </nav>
      </div>

      {/* API Health Indicator - Only visible when sidebar is open */}
      {!isCollapsed && (
        <div className="p-4 border-t border-gray-200">
          <ApiHealthIndicator />
        </div>
      )}
      </div>
    </>
  );
};

export default Sidebar;